import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LANGUAGES = ["English", "हिंदी", "தமிழ்", "తెలుగు", "বাংলা"];

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.2 6 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.2 6 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 34.7 44 29.8 44 24c0-1.3-.1-2.6-.4-3.9z" /></svg>
);

export default function Login() {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("English");
  const [resendIn, setResendIn] = useState(0);
  const otpRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("yaara_welcomed") && !localStorage.getItem("yaara_token")) {
      navigate("/welcome", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (step !== "otp" || resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, resendIn]);

  const sendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone });
      console.log(data, 'data');
      setStep("otp");
      setResendIn(30);
      const serverOtp = data.otp || "123456";
      setGeneratedOtp(serverOtp);

      // Trigger https://loveones.in/sms API with mobile and otp
      try {
        const formData = new FormData();
        formData.append("mobile", phone);
        formData.append("otp", serverOtp);
        await fetch("https://loveones.in/sms", {
          method: "POST",
          body: formData,
        });
      } catch (smsErr) {
        console.warn("SMS send notification:", smsErr);
      }

      toast.success(`OTP sent to +91 ${phone}! (OTP: ${serverOtp})`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp });
      login(data.token, data.user);
      navigate(data.needs_onboarding ? "/onboarding" : "/");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen max-w-md mx-auto flex flex-col relative overflow-hidden">
      {/* Stage-glow hero */}
      <div className="relative h-[34vh] min-h-[230px] shrink-0">
        <img src="/assets/hero-onboard-1.jpg" alt="" className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-70" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(11,11,20,0.25), rgba(11,11,20,0.55) 62%, #0B0B14 100%)" }} />
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-6">
          <h1 data-testid="login-logo" className="font-serif-display font-bold text-[46px] leading-none text-[#FFD86B] drop-shadow-[0_4px_24px_rgba(255,216,107,0.45)] select-none">Yaara</h1>
          <p className="text-[#B8B0C9] text-[13px] mt-2 tracking-wide">India's Own Live Community</p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 px-5 -mt-2 pb-8 flex-1 flex flex-col">
        <div className="rounded-3xl bg-[#14101F]/95 backdrop-blur-xl gold-hairline px-5 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.45)]">
          <p className="text-white text-center font-serif-display font-bold text-[22px] mb-5" data-testid="login-heading">
            Welcome, Yaara <span className="text-[#FFD86B]">✦</span>
          </p>

          {step === "phone" ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-[#1E1830] border border-white/[0.08] rounded-2xl px-4 py-3.5 focus-within:border-[#FFD86B]/50 transition-colors">
                <span className="text-white font-semibold text-sm flex items-center gap-1.5">🇮🇳 +91</span>
                <span className="w-px h-4 bg-white/15" />
                <input
                  data-testid="phone-input"
                  type="tel" maxLength={10} value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  placeholder="98765 43210"
                  className="bg-transparent outline-none flex-1 text-white text-sm font-semibold placeholder:text-[#6E6683] min-w-0"
                />
              </div>
              <button
                data-testid="send-otp-button"
                onClick={sendOtp} disabled={phone.length !== 10 || busy}
                className="btn-gradient w-full py-3.5 text-sm tracking-widest disabled:opacity-40 active:scale-[0.98] transition-transform"
              >
                {busy ? "SENDING…" : "GET OTP"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <label className="text-xs font-semibold text-[#B8B0C9] block text-center">Enter OTP sent to +91 {phone}</label>
              <div className="relative" onClick={() => otpRef.current?.focus()}>
                <div className="flex justify-center gap-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i}
                      className={`w-11 h-13 min-h-[3.1rem] rounded-2xl bg-[#1E1830] border flex items-center justify-center text-white text-xl font-bold transition-colors ${otp[i] ? "border-[#FFD86B]/70" : i === otp.length ? "border-[#FF4D8D]/60" : "border-white/[0.08]"}`}>
                      {otp[i] || ""}
                    </div>
                  ))}
                </div>
                <input
                  ref={otpRef}
                  data-testid="otp-input"
                  type="tel" maxLength={6} value={otp} autoFocus
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="absolute inset-0 w-full h-full opacity-0 caret-transparent"
                />
              </div>
              <p className="text-[11px] text-[#6E6683] text-center">
                Your OTP is <span className="text-[#FFD86B] font-bold">{generatedOtp || "123456"}</span>
                {" · "}
                {resendIn > 0 ? (
                  <span data-testid="otp-resend-timer">Resend in {resendIn}s</span>
                ) : (
                  <button data-testid="otp-resend-button" onClick={sendOtp} className="text-[#FFD86B] font-bold">Resend OTP</button>
                )}
              </p>
              <button
                data-testid="verify-otp-button"
                onClick={verify} disabled={otp.length !== 6 || busy}
                className="btn-gradient w-full py-3.5 text-sm tracking-widest disabled:opacity-40 active:scale-[0.98] transition-transform"
              >
                {busy ? "VERIFYING…" : "VERIFY & CONTINUE"}
              </button>
              <button data-testid="change-phone-button" onClick={() => setStep("phone")} className="w-full text-[#FF4D8D] text-xs font-bold">
                Change number
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-[#6E6683] text-[11px]">· or continue with ·</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="google-login-button" onClick={() => toast("Google login coming soon — use phone OTP for now")}
              className="flex items-center justify-center gap-2 glass-chip rounded-full py-3 text-white text-xs font-bold active:scale-[0.97] transition-transform">
              <GoogleG /> Google
            </button>
            <button data-testid="truecaller-login-button" onClick={() => toast("Truecaller login coming soon — use phone OTP for now")}
              className="flex items-center justify-center gap-2 glass-chip rounded-full py-3 text-white text-xs font-bold active:scale-[0.97] transition-transform">
              <span className="w-4 h-4 rounded-full bg-[#0091EA] flex items-center justify-center"><Phone size={9} className="text-white" /></span> Truecaller
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-5" data-testid="language-row">
            {LANGUAGES.map((l) => (
              <button key={l} data-testid={`lang-${l === "English" ? "english" : l}`} onClick={() => setLang(l)}
                className={`px-3.5 py-1.5 text-[11px] font-bold transition-colors ${lang === l ? "glass-chip-active" : "glass-chip text-[#B8B0C9]"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-[#6E6683] mt-6 text-center">18+ only · By continuing you agree to our <span className="text-[#B8B0C9]">Terms & Privacy Policy</span></p>
      </motion.div>
    </div>
  );
}
